import {
  collection, doc, getDocs, serverTimestamp, writeBatch,
} from 'firebase/firestore';
import { auth, db } from '../../services/firebase';
import {
  buildFinalizedText,
  buildRecipientFacingBrief,
  isJourneyBriefFinalizable,
} from './journeyBriefModel';
import { getFinalizedJourneyAnswers } from './journeyAnswerModel';

const asArray = (value) => Array.isArray(value) ? value : [];
const cleanText = (value = '') => String(value || '').trim();
const timestampValue = (value) => value?.toMillis?.() || value?.seconds * 1000 || 0;

const revisionCollection = (childId, draftId) => collection(
  db, 'children', childId, 'preparationDrafts', draftId, 'revisions'
);

export const listJourneyFinalizedRevisions = async ({ childId, draftId }) => {
  if (!childId || !cleanText(draftId)) return [];
  const snapshot = await getDocs(revisionCollection(childId, draftId));
  return snapshot.docs.map((item) => ({ id: item.id, ...item.data() }))
    .sort((left, right) => timestampValue(right.finalizedAt) - timestampValue(left.finalizedAt));
};

export const buildJourneyFinalizedRevision = ({
  revisionId, childId, ownerUserId, draftIdentity, brief, answers, uid, previousRevisionId,
}) => {
  if (!isJourneyBriefFinalizable(brief)) {
    throw new Error('Confirm at least one answer before finishing this brief.');
  }
  const confirmedAnswers = getFinalizedJourneyAnswers(answers).map((answer) => ({
    questionId: cleanText(answer.questionId),
    confirmedText: String(answer.confirmedText || '').trim(),
    origin: cleanText(answer.origin),
    sourceIds: asArray(answer.sourceIds).filter(Boolean),
  }));
  const recipientContent = buildRecipientFacingBrief(brief);
  const internalSourceReferences = [...new Set(brief.sections.flatMap((section) => (
    section.statements.flatMap((statement) => asArray(statement.sourceIds))
  )))];
  return {
    revisionId,
    journeyId: draftIdentity.draftId,
    journeyDraftId: draftIdentity.draftId,
    personId: childId,
    ownerUserId: cleanText(ownerUserId) || uid,
    templateId: draftIdentity.templateId,
    templateVersion: draftIdentity.templateVersion,
    ...(brief.planId ? {
      planId: brief.planId,
      planVersion: brief.planVersion,
      planFingerprint: brief.planFingerprint,
      executionFingerprint: brief.executionFingerprint,
    } : {}),
    meetingType: draftIdentity.journeyType,
    title: brief.title,
    reportHeader: recipientContent.header || null,
    sections: brief.sections,
    answers: confirmedAnswers,
    internalSourceReferences,
    recipientContent,
    finalizedText: buildFinalizedText(recipientContent),
    answerFingerprint: brief.answerFingerprint,
    createdAt: serverTimestamp(),
    createdBy: uid,
    finalizedAt: serverTimestamp(),
    finalizedBy: uid,
    previousRevisionId: cleanText(previousRevisionId) || null,
  };
};

export const finalizeJourneyBrief = async ({
  childId, ownerUserId, draftIdentity, brief, answers = [], previousRevisionId = '',
}) => {
  if (!childId) throw new Error('Choose a person before finishing this brief.');
  const uid = auth.currentUser?.uid;
  if (!uid) throw new Error('Please sign in before finishing this brief.');
  if (!cleanText(draftIdentity?.draftId) || !cleanText(draftIdentity?.templateId)
    || !Number.isInteger(draftIdentity?.templateVersion) || !cleanText(draftIdentity?.journeyType)) {
    throw new Error('Journey identity is incomplete.');
  }
  const ref = doc(revisionCollection(childId, draftIdentity.draftId));
  const revision = buildJourneyFinalizedRevision({
    revisionId: ref.id,
    childId,
    ownerUserId,
    draftIdentity,
    brief,
    answers,
    uid,
    previousRevisionId,
  });
  const batch = writeBatch(db);
  batch.set(ref, revision);
  batch.set(doc(db, 'children', childId, 'preparationDrafts', draftIdentity.draftId), {
    latestFinalizedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  }, { merge: true });
  await batch.commit();
  return revision;
};
